import { Anchor, Badge, Card, Group, Stack, Text, Tooltip } from '@mantine/core';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import useFetch from '../../helpers/useFetch';

// Mirrors CAPABILITY_LABELS in packages/preferences/tenant-block.ts, so a badge reads the
// way the Store section the model itself gets reads.
const CAPABILITY_LABELS = {
  'products.search': 'product search',
  'documents.search': 'document search',
  'knowledge.search': 'knowledge search',
  'feed.personalized': 'personalized feed',
  'orders.read': 'order lookup',
  'profile.read': 'customer profile',
};

// Profiles v1-v3 still declare documents.search, which the resolver no longer serves (only
// knowledge.search mounts), so counting the two retrieval capabilities separately would report
// every store one short and always list one of them as missing.
const RETRIEVAL = ['knowledge.search', 'documents.search'];

const labelFor = (capability) => CAPABILITY_LABELS[capability] || capability;

const retrievalEntry = (list) => {
  const pair = RETRIEVAL.map((capability) => list.find((entry) => entry.capability === capability)).filter(Boolean);
  const served = pair.find(({ available }) => available);

  return {
    capability: 'retrieval',
    label: labelFor(served ? served.capability : 'knowledge.search'),
    available: Boolean(served),
    note: (served || pair[0])?.note,
  };
};

export const coverageSummary = (capabilities) => {
  const list = capabilities || [];
  const entries = [];

  for (const entry of list) {
    if (!RETRIEVAL.includes(entry.capability)) {
      entries.push({ ...entry, label: labelFor(entry.capability) });
    } else if (!entries.some(({ capability }) => capability === 'retrieval')) {
      entries.push(retrievalEntry(list));
    }
  }

  return {
    entries,
    available: entries.filter(({ available }) => available).length,
    total: entries.length,
    missing: entries.filter(({ available }) => !available),
  };
};

// Only a super admin can reach the connectors screen or the super admin block, so a merchant
// keeps the sentence that names the one thing they can ask for.
export const actionFor = ({ capability }, { isSuperAdmin, accountId, domainId } = {}) => {
  if (capability === 'orders.read') {
    if (isSuperAdmin) {
      return {
        text: "Turn on Order status lookup in the super admin settings, or connect the store's order API",
        // The switch lives in Behaviour & context; the Connectors tab hides the super admin
        // sections, so it cannot be the target.
        to: `/accounts/${accountId}/domains/${domainId}/chatBot?tab=personalization&section=behaviourContext`,
      };
    }
    return { text: 'Turn on Order status lookup in the super admin settings to answer order questions' };
  }
  return { text: 'Not available for this store' };
};

const CapabilityBadge = ({ entry }) => {
  const badge = (
    <Badge size="sm" variant="light" color={entry.available ? 'success' : 'gray'}>
      {entry.label}
    </Badge>
  );

  if (!entry.note) {
    return badge;
  }

  return (
    <Tooltip label={entry.note} multiline maw={260} withArrow>
      {badge}
    </Tooltip>
  );
};

const AgentCoverageCard = ({ fetchUrl, refreshKey, accountId, domainId, isSuperAdmin }) => {
  const fetch = useFetch();

  const [fetched, setFetched] = useState();

  useEffect(() => {
    // A save bumps refreshKey while the previous read may still be in flight; the older
    // block must not land on top of the one that reflects the saved row.
    let live = true;
    // An API that does not serve the block yet must not blank the tab it sits on.
    fetch(`${fetchUrl}/chatBot/tenantBlock`)
      .then((res) => {
        if (live) {
          setFetched(res);
        }
      })
      .catch(() => {});

    return () => {
      live = false;
    };
  }, [fetch, fetchUrl, refreshKey]);

  const { entries, available, total, missing } = coverageSummary(fetched?.tenantBlock?.capabilities);

  if (!total) {
    return null;
  }

  return (
    <Card withBorder mt="sm">
      <Text size="sm" fw={600}>
        Sales chat: {available} of {total} capabilities
      </Text>

      <Group gap={6} mt="xs">
        {entries.map((entry) => (
          <CapabilityBadge key={entry.capability} entry={entry} />
        ))}
      </Group>

      {missing.length > 0 && (
        <Stack gap={2} mt="xs">
          {missing.map((entry) => {
            const { text, to } = actionFor(entry, { isSuperAdmin, accountId, domainId });

            return to ? (
              <Anchor key={entry.capability} component={Link} to={to} size="xs">
                {text}
              </Anchor>
            ) : (
              <Text key={entry.capability} size="xs" c="dimmed">
                {text}
              </Text>
            );
          })}
        </Stack>
      )}
    </Card>
  );
};

export default AgentCoverageCard;
